import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { Check, Loader2, Lock, Mail, ShieldCheck } from "lucide-react";
import {
  AuthCard,
  AuthCardPage,
  AuthField,
  AuthSplit,
  BackToSignIn,
  PasswordInput,
  PasswordStrength,
  authInputClass,
  authPrimaryButton,
} from "@/components/auth/AuthLayout";
import { dateLocale, withClock } from "@/lib/dateTime";

type LinkInfo = { purpose: "INVITE" | "RESET"; email: string; first_name?: string; expires_at: string; min_length: number };

/** Reads the link secret once from the address bar (#token=…) and removes it, so it stays out of history */
function takeTokenFromUrl(): string {
  const params = new URLSearchParams(window.location.hash.replace(/^#/, ""));
  const token = params.get("token") || "";
  if (token) window.history.replaceState(null, "", window.location.pathname);
  return token;
}

/** What a new person gets from joining */
const JOIN_POINTS = [
  "Your day plan on one screen",
  "WhatsApp and email next to every customer",
  "Know the moment a customer opens your proposal",
];

function JoinAside({ info }: { info: LinkInfo }) {
  const expires = new Date(info.expires_at);
  return (
    <>
      <div className="space-y-4 max-w-[440px]">
        <p className="text-xs font-medium tracking-[0.14em] uppercase text-[#A9B0E8]">Welcome to the team</p>
        <h2 className="text-4xl font-semibold tracking-tight leading-[1.12]">
          Two minutes and
          <br />
          you’re in{info.first_name ? `, ${info.first_name}` : ""}.
        </h2>
        <p className="text-[15.5px] text-[#C3C8F0] leading-relaxed">
          Leads from IndiaMART and the website start reaching you as soon as your account is ready.
        </p>
      </div>
      <ul className="space-y-3 text-[#DCE0FA]">
        {JOIN_POINTS.map((p) => (
          <li key={p} className="flex items-center gap-2.5">
            <span className="size-6 rounded-full bg-[#1F7A4D] flex items-center justify-center shrink-0">
              <Check className="size-3 text-white" strokeWidth={3} />
            </span>
            {p}
          </li>
        ))}
      </ul>
      <div className="flex-1" />
      <span className="text-xs text-[#8C93C4]">
        Invite link works until{" "}
        {expires.toLocaleString(dateLocale(), withClock({ day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }))}
      </span>
    </>
  );
}

/**
 * Where invite and password-reset links land. The link is checked first (who it is for, still valid?),
 * then the user chooses their password; the link stops working once used.
 */
export function SetPasswordPage() {
  const navigate = useNavigate();
  const { error: toastError, success: toastSuccess } = useToast();
  const [token] = useState(takeTokenFromUrl);
  const [info, setInfo] = useState<LinkInfo | null>(null);
  const [linkError, setLinkError] = useState<string | null>(token ? null : "This link is incomplete. Open it again from your email.");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) return;
    api
      .post("/api/auth/password-link/check", { token })
      .then((res) => setInfo(res as LinkInfo))
      .catch((err: any) => setLinkError(err.message || "This link is invalid or has expired."));
  }, [token]);

  const minLength = info?.min_length || 8;
  const isInvite = info?.purpose === "INVITE";
  const matches = confirmPassword.length > 0 && password === confirmPassword;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password.length < minLength) {
      toastError("Password too short", `Your password must be at least ${minLength} characters.`);
      return;
    }
    if (password !== confirmPassword) {
      toastError("Passwords do not match", "Type the same password in both fields.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/api/auth/password-link/set", { token, password });
      toastSuccess("Password saved", "You can now sign in with your new password.");
      navigate("/login", { replace: true });
    } catch (err: any) {
      toastError("Could not save password", err.message || "Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (linkError) {
    return (
      <AuthCardPage topRight={<BackToSignIn />}>
        <AuthCard
          tone="warning"
          status="Link expired"
          title="This link has expired"
          subtitle="Reset and invite links work once, for a short time. Get a fresh one below."
        >
          <p className="rounded-xl bg-warning-soft border border-warning/40 px-3 py-2.5 text-[12.5px] text-warning">
            {linkError} Invite links last 72 hours and reset links 30 minutes.
          </p>
          <Link to="/forgot-password" className={authPrimaryButton}>
            Send me a new reset link
          </Link>
          <p className="text-xs text-muted-foreground text-center">New to the CRM? Ask your administrator to resend your invite.</p>
        </AuthCard>
      </AuthCardPage>
    );
  }

  if (!info) {
    return (
      <AuthCardPage>
        <Loader2 className="size-6 animate-spin text-primary" aria-label="Checking your link" />
      </AuthCardPage>
    );
  }

  const form = (
    <form onSubmit={handleSubmit} className="w-full space-y-4">
      {/* Lets password managers save the new password against the right account */}
      <input type="text" name="username" autoComplete="username" value={info.email} readOnly hidden />
      <AuthField id="email" label="Work email" icon={<Mail />} right={<span className="pr-2 text-xs text-muted-foreground">from your link</span>}>
        <input id="email" type="email" value={info.email} readOnly className={`${authInputClass} text-muted-foreground`} />
      </AuthField>
      <AuthField id="password" label={isInvite ? "Create password" : "New password"} icon={<Lock />}>
        <PasswordInput id="password" value={password} onChange={setPassword} autoComplete="new-password" autoFocus />
      </AuthField>
      <PasswordStrength password={password} minLength={minLength} />
      <AuthField
        id="confirmPassword"
        label="Type it again"
        icon={<Lock />}
        tone={confirmPassword ? (matches ? "ok" : "bad") : undefined}
        right={confirmPassword ? <span className={`pr-2 text-xs font-semibold ${matches ? "text-success" : "text-danger"}`}>{matches ? "Matches" : "Doesn’t match"}</span> : null}
      >
        <PasswordInput id="confirmPassword" value={confirmPassword} onChange={setConfirmPassword} autoComplete="new-password" />
      </AuthField>
      <p className="flex gap-2 text-xs text-muted-foreground">
        <ShieldCheck className="size-4 shrink-0 text-primary" />
        Common or leaked passwords, and your name or email, are not accepted.
      </p>
      <button type="submit" disabled={submitting} className={authPrimaryButton}>
        {submitting ? <Loader2 className="size-4 animate-spin" /> : isInvite ? "Create account" : "Save and sign in"}
      </button>
    </form>
  );

  if (isInvite) {
    return (
      <AuthSplit
        aside={<JoinAside info={info} />}
        topRight={
          <span>
            Already have an account?{" "}
            <Link to="/login" className="font-medium text-primary hover:underline">
              Sign in
            </Link>
          </span>
        }
      >
        <div className="w-full max-w-[420px] space-y-4">
          <div className="space-y-1.5">
            <h2 className="text-[28px] font-semibold tracking-tight">Set up your account</h2>
            <p className="text-sm text-muted-foreground">Choose a password only you know.</p>
          </div>
          {form}
        </div>
      </AuthSplit>
    );
  }

  return (
    <AuthCardPage topRight={<span className="text-muted-foreground">{info.email}</span>}>
      <AuthCard title="Choose a new password" subtitle="You’ll sign in with it straight after.">
        {form}
      </AuthCard>
    </AuthCardPage>
  );
}
